import { useEffect, useRef, useState } from 'react';
import RadioFilterPopup from './subcomponents/radio-filter-popup/RadioFilterPopup.tsx';
import RadioFilterButton from './subcomponents/radio-filter-button/RadioFilterButton.tsx';
import TextFilter from './subcomponents/text-filter/TextFilter.tsx';
import TableBefore from '../table-before/TableBefore.tsx';
import NoResults from '../no-results/NoResults.tsx';

export type RegulatoryFilterProps = {
	fundTypeLabels: string[];
	requestUrl: string;
	selectedFilter: string;
};

const RegulatoryFilter: React.FC<RegulatoryFilterProps> = ({
	fundTypeLabels,
	requestUrl,
	selectedFilter,
}) => {
	const [fundTypeFilter, setFundTypeFilter] = useState<string>(
		selectedFilter ?? fundTypeLabels[0]
	);
	const [keyWordFilter, setKeyWordFilter] = useState<string>('');
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [showNoResults, setShowNoResults] = useState<boolean>(false);
	const [keyWordFilterActive, setKeyWordFilterActive] =
		useState<boolean>(false);

	const liveRegionRef = useRef<HTMLDivElement>(null);
	const mainDivRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		window.history.replaceState(null, '', requestUrl);
		filterByFundType();
		const overlay: HTMLDivElement = document.getElementById(
			'overlay'
		) as HTMLDivElement;
		overlay.hidden = true;
		if (liveRegionRef.current) {
			liveRegionRef.current.innerText = 'Content loaded';
		}
		if (mainDivRef.current) {
			mainDivRef.current.hidden = false;
		}
	}, []);

	const handleFundTypeFilterChange = (newFundTypeFilter: string) => {
		setFundTypeFilter(newFundTypeFilter);
	};

	const handleKeyWordFilterChange = (newKeyWordFilter: string) => {
		setKeyWordFilter(newKeyWordFilter);
	};

	const handleErrorMessageChange = (newErrorMessage: string) => {
		setErrorMessage(newErrorMessage);
	};

	const handleFundTypeAnsweredPopup = async (): Promise<void> => {
		resetKeywordFilter();
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		filterByFundType();
		if (keyWordFilter.length > 0) {
			handleAnsweredWithInputVal(keyWordFilter);
		} else {
			setErrorMessage('');
			setKeyWordFilter('');
		}
	};

	const filterByFundType = async (): Promise<void> => {
		filterByFundTypeWithRadioVal('---');
	};

	const handleFundTypeAnsweredWithRadioVal = async (
		value: string
	): Promise<void> => {
		const overlay: HTMLDivElement = document.getElementById(
			'overlay'
		) as HTMLDivElement;
		overlay.hidden = false;
		if (liveRegionRef.current) {
			liveRegionRef.current.innerText = 'Loading new results, please wait';
		}
		setTimeout(() => {
			resetKeywordFilter();
			filterByFundTypeWithRadioVal(value);
			if (keyWordFilter.length > 0) {
				handleAnsweredWithInputVal(keyWordFilter);
			} else {
				setErrorMessage('');
				setKeyWordFilter('');
			}
		}, 0);
	};
	const filterByFundTypeWithRadioVal = async (value: string): Promise<void> => {
		const overlay = document.getElementById('overlay') as HTMLDivElement;
		overlay.hidden = false;
		const elements = document.querySelectorAll('[data-fundtype-labels]');
		elements.forEach((element) => {
			const fundAttributes = element
				.getAttribute('data-fundtype-labels')
				?.split(',');
			if (
				!(value !== '---'
					? fundAttributes?.includes(value)
					: fundAttributes?.includes(fundTypeFilter)) &&
				element instanceof HTMLElement
			) {
				element.style.display = 'none';
			}
		});
		overlay.hidden = true;
		if (liveRegionRef.current) {
			liveRegionRef.current.innerText = 'Content loaded';
		}
	};

	const handleKeyWordFilterAnsweredWithVal = async (
		value: string
	): Promise<void> => {
		resetKeywordFilterWithFundTypeFilter();
		setTimeout(() => {
			handleAnsweredWithInputVal(value);
		}, 0);
	};

	const handleAnsweredWithInputVal = async (value: string): Promise<void> => {
		if (liveRegionRef.current) {
			liveRegionRef.current.innerText = 'Search result filtered';
		}
		const tableContainer: HTMLDivElement = document.getElementById(
			'gls-table-container'
		) as HTMLDivElement;

		const valLower = value.toLowerCase();
		let emptySearch = true;
		const elements: NodeListOf<HTMLElement> = document.querySelectorAll(
			'[data-fundtype-labels]'
		);
		elements.forEach((element) => {
			const fundName = (
				element.getAttribute('data-fund-name') ?? ''
			).toLowerCase();
			const ticker = (
				element.getAttribute('data-nasdaq-ticker') ?? ''
			).toLowerCase();
			if (!(ticker.includes(valLower) || fundName.includes(valLower))) {
				element.style.display = 'none';
			} else if (element.style.display !== 'none') {
				emptySearch = false;
			}
		});
		setShowNoResults(emptySearch);
		tableContainer.hidden = emptySearch;
		setKeyWordFilterActive(true);
	};

	const resetKeywordFilter = async (): Promise<void> => {
		const elements = document.querySelectorAll('[data-fund-name]');
		elements.forEach((element) => {
			if (element instanceof HTMLElement) {
				element.style.display = '';
			}
		});
		setShowNoResults(false);
		setKeyWordFilterActive(false);
		const tableContainer: HTMLDivElement = document.getElementById(
			'gls-table-container'
		) as HTMLDivElement;
		tableContainer.hidden = false;
	};

	const resetKeywordFilterWithFundTypeFilter = async (): Promise<void> => {
		resetKeywordFilter();
		filterByFundTypeWithRadioVal('---');
	};

	const resetAllFilters = async (): Promise<void> => {
		const overlay: HTMLDivElement = document.getElementById(
			'overlay'
		) as HTMLDivElement;
		overlay.hidden = false;
		setTimeout(() => {
			setKeyWordFilter('');
			setFundTypeFilter(fundTypeLabels[0]);
			resetKeywordFilter();
			filterByFundTypeWithRadioVal(fundTypeLabels[0]);
		}, 0);
	};

	return (
		<>
			<div
				aria-live="polite"
				aria-atomic="true"
				ref={liveRegionRef}
				style={{
					position: 'absolute',
					left: '-9999px',
					width: '1px',
					height: '1px',
					overflow: 'hidden',
				}}
			></div>
			<div
				ref={mainDivRef}
				className="filter-and-search-container"
				hidden={true}
			>
				<div className="dynamic-tiles-list gls-library-filter">
					<section className="fundTypeSection row">
						<div className="col-sm-10 mobile-only">
							<RadioFilterPopup
								onFundTypeFilterChange={handleFundTypeFilterChange}
								handleAnswered={handleFundTypeAnsweredPopup}
								labels={fundTypeLabels}
								selectedFilter={fundTypeFilter}
							/>
						</div>
						<div className="col-lg-7 col-md-8 col-sm-10 mobile-hidden">
							<RadioFilterButton
								onFundTypeFilterChange={handleFundTypeFilterChange}
								handleAnswered={handleFundTypeAnsweredWithRadioVal}
								labels={fundTypeLabels}
								selectedFilter={fundTypeFilter}
							/>
						</div>
					</section>
				</div>
				<div className="dynamic-tiles-list gls-library-search">
					<TextFilter
						onErrorMessageChange={handleErrorMessageChange}
						onKeyWordFilterChange={handleKeyWordFilterChange}
						handleAnswered={handleKeyWordFilterAnsweredWithVal}
						handleResetFilter={resetKeywordFilterWithFundTypeFilter}
						errorMessage={errorMessage}
						keywordFilterActive={keyWordFilterActive}
						keyWordFilterVal={keyWordFilter}
					/>
				</div>
			</div>
			<TableBefore
				isFilterActive={
					fundTypeFilter !== fundTypeLabels[0] || keyWordFilterActive
				}
				handleResetFilter={resetAllFilters}
			/>
			<NoResults
				show={showNoResults}
				keyWord={keyWordFilter}
				handleResetFilter={resetAllFilters}
			/>
		</>
	);
};

export default RegulatoryFilter;
